import * as React from 'react';
import { useForm, useWatch } from 'react-hook-form';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    FieldError,
    Form,
    Submit,
    TextField,
    TextInput,
} from '@gitbook/spine-react';
import { isHttpUrl } from '@gitbook/util-url';

import type { InsertFilesAccept } from './state';

type TabURLFormInputs = {
    url: string;
};

/**
 * Tab to enter a custom file URL.
 */
export function TabURL(props: {
    accept: InsertFilesAccept;
    onSelect: (urls: api.ContentRefURL[]) => void;
}): React.ReactElement {
    const { accept, onSelect } = props;

    const form = useForm<TabURLFormInputs>({
        values: {
            url: '',
        },
    });

    // Preload the image URl to improve performance when inserting
    const imageUrl = useWatch({
        control: form.control,
        name: 'url',
    });
    React.useEffect(() => {
        if (imageUrl && accept === 'images' && isHttpUrl(imageUrl)) {
            const img = new Image();
            img.src = imageUrl;
            return () => {
                img.src = '';
            };
        }
    }, [imageUrl, accept]);

    const onSubmit = () => {
        const { url } = form.getValues();
        if (!isHttpUrl(url)) {
            return;
        }

        onSelect([{ kind: 'url', url }]);
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody>
                <TextField
                    name="url"
                    validate="http-url"
                    required
                    minLength={1}
                    maxLength={2048}
                    className="my-4"
                >
                    <TextInput autoFocus placeholder="e.g. https://mysite.com/file" type="url" />
                    <FieldError />
                </TextField>
                {accept !== 'files' ? (
                    <div className="mt-2 flex items-center gap-2">
                        <Icon.InfoCircle size={16} className="text-primary" />
                        <div className="flex flex-1">
                            <p className="text-muted text-ui-small">
                                {accept === 'openapi' ? 'Supported formats: JSON or YAML' : null}
                                {accept === 'images'
                                    ? 'Supported formats: PNG, JPG, GIF, SVG'
                                    : null}
                            </p>
                        </div>
                    </div>
                ) : null}
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Submit label="Insert" />
            </DialogFooter>
        </Form>
    );
}
